import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ToastMessage } from '@utility/toast';
import {
  MaskedCurrencyFormField,
  RangeDateFormField,
} from '@elements/FormField';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import WalletService from '@utility/services/wallet';
import { useEffect, useMemo, useState } from 'react';
import { CommonUtility } from '@utility/common';
import { LoaderBar } from '@components/LoaderBar';
import { AppModal } from '@elements/Modal';
import { Button } from '@skupreme/shared/ui';
import { ErrorMessageConstant } from '@utility/constants/common';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { getStripe } from '@utility/stripe-load';
import { DateUtility } from '@utility/date';
import { PaymentDialog } from './PaymentDialog';

const balanceScheme = yup.object().shape({
  balance: yup.number().required().positive().integer(),
});

function BalancePopup({ open, closeModal }) {
  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(balanceScheme),
    shouldUnregister: true,
  });

  const stripe = useStripe();
  const elements = useElements();

  const balances = useMemo(() => [25, 50, 75, 100], []);

  const [saving, setSaving] = useState(false);

  const onSubmit = async data => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    try {
      setSaving(true);
      const res = await WalletService.addBalance(data);
      if (res?.data?.requires_next_action) {
        const { paymentIntent } = await stripe.confirmCardPayment(
          res.data.client_secret,
        );
        await WalletService.confirmBalance({
          // @ts-expect-error - TS18048: 'paymentIntent' is possibly 'undefined'.
          payment_intent_id: paymentIntent.id,
        });
      }

      ToastMessage.success('Balance added successfully');
      closeModal(data.balance);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const btnClicked = item => {
    // @ts-expect-error - TS2345: Argument of type '"balance"' is not assignable to parameter of type 'never'.
    setValue('balance', item);
  };

  return (
    <PaymentDialog
      open={open}
      onClose={() => closeModal()}
      title="Add Balance"
      aria-label="Add Balance"
      size="md"
    >
      {saving && <LoaderBar content="Adding Balance" />}
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <MaskedCurrencyFormField
          errors={errors.balance}
          name="balance"
          label="Balance"
          control={control}
          // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null | undefined'.
          defaultValue=""
        />
        <div className="sk:flex sk:flex-wrap sk:gap-1">
          {balances.map(item => (
            <HoverBorderButton
              key={item}
              type="button"
              onClick={() => btnClicked(item)}
            >
              {CommonUtility.currencyFormat(item)}
            </HoverBorderButton>
          ))}
        </div>
        <div className="sk:mt-4 sk:flex sk:items-center sk:justify-end sk:gap-2">
          <Button onPress={() => closeModal()}>Cancel</Button>
          <StyledButton type="submit">Submit</StyledButton>
        </div>
      </form>
    </PaymentDialog>
  );
}

export function AddBalancePopup({ open, closeModal }) {
  return (
    <Elements stripe={getStripe()}>
      <BalancePopup open={open} closeModal={closeModal} />
    </Elements>
  );
}

const transactionHistorySchema = yup.object().shape({
  date: yup
    .array()
    .required('Date is required')
    .typeError('Date is required')
    .min(2, 'Date is required'),
});

export function ExportTransactionPopup({ closeModal, open, type = '' }) {
  const [loading, setLoading] = useState(false);

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(transactionHistorySchema),
    shouldUnregister: true,
  });

  const onSubmit = async formValues => {
    try {
      setLoading(true);
      const payload = {
        start_date: DateUtility.getUTCDateStartTime(formValues.date[0]),
        end_date: DateUtility.getUTCDateEndTime(formValues.date[1]),
      };
      // @ts-expect-error - TS2339: Property 'type' does not exist on type '{ start_date: string; end_date: string; ...
      if (['subscription'].includes(type)) payload.type = type;

      const res = await WalletService.getTransactionPDFs(payload);
      if (res?.data) {
        window.open(res.data, '_blank');
      }
      closeModal();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // @ts-expect-error - TS2345: Argument of type '{ date: Date[]; }' is not assignable to parameter of type 'Ass...
    reset({
      date: [DateUtility.addDays(new Date(), -1), new Date()],
    });
  }, [open]);

  return (
    <AppModal
      open={open}
      closeModal={() => closeModal()}
      size="tiny"
      title={`Export ${
        ['subscription'].includes(type)
          ? 'Subscription Receipts'
          : 'Transaction'
      }`}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          {/* @ts-expect-error - TS2739: Type '{ label: string; control: Control<AssertsShape<Assign<... */}
          <RangeDateFormField
            label="Date Range"
            control={control}
            name="date"
            error={errors?.date}
            hint={errors?.date?.message}
            selectsRange
            resetUTC={false}
            maxDate={new Date()}
          />
        </div>
        <div className="sk:flex sk:justify-end sk:gap-1">
          <HoverBorderButton
            disabled={loading}
            type="button"
            onClick={closeModal}
          >
            Cancel
          </HoverBorderButton>
          <StyledButton loading={loading} type="submit">
            Export
          </StyledButton>
        </div>
      </form>
    </AppModal>
  );
}
